import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRightIcon, HomeIcon } from '@heroicons/react/outline';

export interface BreadcrumbSegment {
  label: string;
  /** Heroicons-style SVG component, shown before the label (same pattern as Dashboard home icon). */
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  href?: string;
  /** Use for in-app navigation without changing the URL (e.g. closing a detail view). Takes precedence over href. */
  onNavigate?: () => void;
}

interface BreadcrumbProps {
  segments: BreadcrumbSegment[];
  className?: string;
  /**
   * When true (default), shows Dashboard (with home icon) as the root before segments.
   * Set false for primary nav destinations (Sponsor Intel, Support, About, Profile)
   * so the current section is the start of the trail.
   */
  prependDashboard?: boolean;
}

const Breadcrumb: React.FC<BreadcrumbProps> = ({
  segments,
  className = '',
  prependDashboard = true,
}) => {
  const segmentInner = (seg: BreadcrumbSegment, opts: { isCurrent: boolean }) => {
    const Icon = seg.icon;
    const textClass = opts.isCurrent
      ? 'font-medium text-text-primary truncate min-w-0'
      : 'truncate min-w-0';
    return (
      <span className="inline-flex items-center gap-1 max-w-[200px] min-w-0">
        {Icon && (
          <Icon
            className={`h-3.5 w-3.5 flex-shrink-0 ${opts.isCurrent ? 'text-text-primary' : 'text-current'}`}
            aria-hidden
          />
        )}
        <span className={textClass}>{seg.label}</span>
      </span>
    );
  };

  return (
    <nav aria-label="Breadcrumb" className={`flex items-center gap-1.5 text-caption ${className}`}>
      {prependDashboard && (
        <Link
          to="/app/dashboard"
          className="flex items-center gap-1 text-text-muted hover:text-bearriBlue transition-colors min-w-0 max-w-[200px]"
        >
          <HomeIcon className="h-3.5 w-3.5 flex-shrink-0" />
          <span className="hidden sm:inline truncate">Dashboard</span>
        </Link>
      )}

      {segments.map((seg, i) => {
        const isLast = i === segments.length - 1;
        const linkClass =
          'text-left bg-transparent border-0 p-0 font-inherit cursor-pointer text-text-muted hover:text-bearriBlue transition-colors max-w-[200px] inline-flex items-center min-w-0';
        return (
          <React.Fragment key={i}>
            {(prependDashboard || i > 0) && (
              <ChevronRightIcon className="h-3 w-3 text-text-disabled flex-shrink-0" />
            )}
            {isLast ? (
              segmentInner(seg, { isCurrent: true })
            ) : seg.onNavigate ? (
              <button type="button" onClick={seg.onNavigate} className={linkClass}>
                {segmentInner(seg, { isCurrent: false })}
              </button>
            ) : seg.href ? (
              <Link to={seg.href} className={linkClass}>
                {segmentInner(seg, { isCurrent: false })}
              </Link>
            ) : (
              segmentInner(seg, { isCurrent: true })
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
};

export default Breadcrumb;
